import React from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { getGetFacesQueryOptions, useGetCard } from '@vlabs/api/artemy/client';
import { DeleteCardsButtonPoint3 } from '@vlabs/monokit/features/delete-cards';
import { useCardIdFromLocationPoint3 } from '@vlabs/monokit/shared/lib/use-card-id-from-location';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';
import { PageHeader } from '@vlabs/monokit/shared/ui/page';
import { useHasPermission } from '@vlabs/permissions';
import { routes } from '@vlabs/shared/navigation/routes';
import { Breadcrumbs, Breadcrumb } from '@vlabs/uikit';
import { useTranslation } from 'react-i18next';
import { generatePath, useHistory } from 'react-router-dom';

import { CreateCardFace } from '../../create-card-face/CreateCardFace';
// ponytail: search removed from point3, restore when search page is added back
// import { SearchByExternalIdButton } from '../../search-by-external-id';
import type { TPointCard } from '../../types';

const displayName = 'CardPageHeader';
const testid = buildTestid(displayName);

function CardPageHeader(): React.JSX.Element {
  const cardId = useCardIdFromLocationPoint3();
  const { t } = useTranslation();
  const getCard = useGetCard<TPointCard>(cardId);
  const queryClient = useQueryClient();
  const history = useHistory();
  const canCreateFace = useHasPermission('card', 'create');
  const canDeleteCard = useHasPermission('card', 'delete');

  const breadcrumbs = (
    <Breadcrumbs>
      <Breadcrumb
        caption={t('card-page:breadcrumbs.cards')}
        href={generatePath(routes.cardsAndLists.index)}
      />
      <Breadcrumb caption={t('card-page:breadcrumbs.card_page')} />
    </Breadcrumbs>
  );

  return (
    <PageHeader
      {...testid()}
      actions={(
        <React.Fragment>
          {!!canCreateFace && (
            <CreateCardFace
              cardId={cardId}
              onSuccess={() => {
                queryClient.invalidateQueries(getGetFacesQueryOptions({ card_ids: [cardId] }));
              }}
              title={t('face') as unknown as string}
            />
          )}
          {/* ponytail: search removed from point3, restore when search page is added back */}
          {/* <SearchByExternalIdButton externalId={cardId} /> */}
          {!!canDeleteCard && (
            <DeleteCardsButtonPoint3
              cardIds={[cardId]}
              onDeleteCards={() => {
                history.push(generatePath(routes.cardsAndLists.activeTab, { activeTab: 'cards' }));
              }}
              variant="outlined"
            />
          )}
        </React.Fragment>
      )}
      breadcrumbs={breadcrumbs}
      title={t('card-page:page_title', { fio: getCard.data?.user_data?.fio ?? '-' })}
    />
  );
}

export { CardPageHeader };
